import { useState } from 'react';
import { ChevronRight, ExternalLink } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { Badge, Button, Card, PageHeader } from '../components/ui';
import {
  useCitizenWorkspaceId,
  useNews,
  useCitizenOutreachItems,
  useTrackedBills,
  useWeekCalendar,
  type CalendarEvent,
} from '../hooks/useCitizenData';
import { fmtDateShort } from '../lib/utils';

type Tab = 'engagement' | 'issues';

export default function BriefingScreen() {
  const navigate = useNavigate();
  const [tab, setTab] = useState<Tab>('engagement');

  return (
    <div className="flex flex-col screen-enter">
      <PageHeader title="Your Briefing" onBack={() => navigate('/citizen/home')} />

      <div className="px-6 py-2">
        <p className="text-xs text-[color:var(--muted-foreground)] leading-relaxed mb-5">
          A daily snapshot of what's happening on the issues you follow — governed by the Citizen Standard.
        </p>

        <div className="rounded-full p-1 flex" style={{ background: 'color-mix(in oklab, var(--secondary), transparent 50%)' }}>
          {(
            [
              { id: 'engagement', label: 'Engagement' },
              { id: 'issues', label: 'Issues' },
            ] as Array<{ id: Tab; label: string }>
          ).map((t) => (
            <button
              key={t.id}
              onClick={() => setTab(t.id)}
              className="flex-1 rounded-full py-2.5 text-sm font-semibold transition-colors"
              style={
                tab === t.id
                  ? { background: 'var(--card)', boxShadow: '0 1px 2px rgba(0,0,0,0.06)', color: 'var(--foreground)' }
                  : { background: 'transparent', color: 'var(--muted-foreground)' }
              }
            >
              {t.label}
            </button>
          ))}
        </div>
      </div>

      <div className="flex-1 px-6 pb-24">{tab === 'engagement' ? <EngagementTab /> : <IssuesTab />}</div>
    </div>
  );
}

function isToday(dateStr: string): boolean {
  const d = new Date(dateStr);
  const now = new Date();
  return d.getFullYear() === now.getFullYear() && d.getMonth() === now.getMonth() && d.getDate() === now.getDate();
}

function EngagementTab() {
  const navigate = useNavigate();
  const workspaceId = useCitizenWorkspaceId();
  const { data: outreach } = useCitizenOutreachItems(workspaceId);
  const { data: tracked } = useTrackedBills(workspaceId);
  const { data: weekEvents } = useWeekCalendar();
  const { data: news } = useNews();

  const openRequests = (outreach || []).filter((o) => {
    const s = (o.status || '').toLowerCase();
    return s === 'active' || s === 'launched';
  }).length;

  const todayEvents = (weekEvents || []).filter((e) => isToday(e.reading_date));

  return (
    <div className="space-y-6 mt-2 anim-slide">
      {/* Snapshot */}
      <div>
        <div className="flex items-baseline justify-between border-b pb-2 mb-4" style={{ borderColor: 'var(--border)' }}>
          <h3 className="text-xl font-extrabold">Today's snapshot</h3>
          <span className="text-xs text-[color:var(--muted-foreground)]">{fmtDateShort(new Date())}</span>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <Card className="p-4 border-0 cursor-pointer" onClick={() => navigate('/citizen/requests')}>
            <div className="text-3xl font-extrabold" style={{ color: 'var(--primary)' }}>
              {openRequests}
            </div>
            <p className="text-[11px] text-[color:var(--muted-foreground)] font-bold uppercase tracking-widest mt-1">
              Open requests
            </p>
          </Card>
          <Card className="p-4 border-0">
            <div className="text-3xl font-extrabold" style={{ color: 'var(--primary)' }}>
              {tracked?.length || 0}
            </div>
            <p className="text-[11px] text-[color:var(--muted-foreground)] font-bold uppercase tracking-widest mt-1">
              Issues tracked
            </p>
          </Card>
        </div>
      </div>

      {/* Today's schedule */}
      {todayEvents.length > 0 && (
        <div>
          <h3 className="text-xl font-extrabold border-b pb-2 mb-4" style={{ borderColor: 'var(--border)' }}>
            Today's schedule
          </h3>
          <div className="space-y-3">
            {todayEvents.slice(0, 5).map((e) => (
              <ScheduleRow key={e.calendar_id} e={e} />
            ))}
          </div>
        </div>
      )}

      {/* In the news */}
      {news && news.length > 0 && (
        <div>
          <h3 className="text-xl font-extrabold border-b pb-2 mb-4" style={{ borderColor: 'var(--border)' }}>
            In the news
          </h3>
          <div className="space-y-2">
            {news.slice(0, 5).map((n, i) => (
              <a
                key={i}
                href={n.link}
                target="_blank"
                rel="noreferrer"
                className="block p-4 rounded-2xl border hover-elevate active-elevate-2"
                style={{ background: 'var(--card)', borderColor: 'var(--border)' }}
              >
                <p className="text-sm font-semibold leading-tight line-clamp-2">{n.title}</p>
                <p className="text-xs text-[color:var(--muted-foreground)] mt-1.5 flex items-center gap-1">
                  {n.source && <span>{n.source}</span>}
                  <ExternalLink className="w-3 h-3" />
                </p>
              </a>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function ScheduleRow({ e }: { e: CalendarEvent }) {
  const time = e.reading_time
    ? new Date(`1970-01-01T${e.reading_time}`).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })
    : 'TBD';
  return (
    <Card className="p-4 border-0 flex gap-4 items-start">
      <div
        className="flex flex-col items-center min-w-[3.5rem] pt-1 border-r pr-4"
        style={{ borderColor: 'var(--border)' }}
      >
        <span className="text-sm font-bold text-center leading-tight" style={{ color: 'var(--primary)' }}>
          {time}
        </span>
      </div>
      <div className="flex-1 min-w-0">
        <h4 className="font-semibold text-sm leading-tight">{e.reading_title}</h4>
        {e.bills?.[0] && <p className="text-xs text-[color:var(--muted-foreground)] mt-0.5">{e.bills[0].number}</p>}
        {(e.derived_location || e.chamber) && (
          <Badge variant="secondary" className="mt-2 rounded-full">
            {e.derived_location || e.chamber}
          </Badge>
        )}
      </div>
    </Card>
  );
}

function IssuesTab() {
  const navigate = useNavigate();
  const workspaceId = useCitizenWorkspaceId();
  const { data: tracked, isLoading } = useTrackedBills(workspaceId);

  return (
    <div className="space-y-3 mt-2 anim-slide">
      <div className="flex items-baseline justify-between border-b pb-2" style={{ borderColor: 'var(--border)' }}>
        <h3 className="text-xl font-extrabold">Tracked issues</h3>
        <span className="text-xs text-[color:var(--muted-foreground)]">{fmtDateShort(new Date())}</span>
      </div>

      {isLoading && <p className="text-sm text-[color:var(--muted-foreground)] py-6">Loading…</p>}

      {!isLoading && (!tracked || tracked.length === 0) && (
        <Card className="p-5 border-0 text-center" style={{ background: 'transparent', borderStyle: 'dashed', borderWidth: 1 }}>
          <p className="text-sm text-[color:var(--muted-foreground)]">
            Nothing tracked yet — tap the heart on any issue to follow it.
          </p>
        </Card>
      )}

      {(tracked || []).map((b: any) => (
        <Card key={b.bill_id || b.id} className="p-5 border-0">
          <div className="flex justify-between items-start mb-3">
            <div className="min-w-0">
              <h4 className="font-semibold text-sm leading-tight">{b.title || b.bill_title}</h4>
              <p className="text-xs text-[color:var(--muted-foreground)] mt-1">{b.number || b.bill_number}</p>
            </div>
          </div>
          <Button
            variant="outline"
            size="sm"
            className="rounded-full"
            onClick={() => navigate(`/citizen/bill/${b.bill_id || b.id}`)}
          >
            View details <ChevronRight className="w-3.5 h-3.5" />
          </Button>
        </Card>
      ))}
    </div>
  );
}
